<template>
    <div>
        <div class="flex justify-between flex-col-reverse sm:flex-row sm:items-center">
            <div class="flex sm:items-center flex-col sm:flex-row">
                <table class="mx-2">
                    <tr>
                        <th scope="row" class="text-right text-sm whitespace-nowrap">{{ $t('commons.version') }} :</th>
                        <td class="px-3">
                            <app-select
                                :label="$t('commons.version')"
                                :value="amendment.id"
                                class=""
                                :show-label="false"
                                :disabled="!amendment.id"
                                @change="onVersionSelected($event)"
                            >
                                <option
                                    :value="version.id"
                                    v-for="(version, index) in amendment.versions"
                                    :key="version.id"
                                >
                                    {{ amendment.versions.length - index }} ({{ version.createdAt | humanizeDate }})
                                </option>
                            </app-select>
                        </td>
                    </tr>
                </table>

                <div class="m-2 sm:my-0">
                    <app-button @click="onNewVersion" :disabled="!amendment.id">
                        {{ $t('commons.newVersion') }}
                    </app-button>
                </div>
            </div>
        </div>
        <ValidationObserver v-slot="{ dirty }" ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-4" :label="$t('commons.infos')">
                    <app-input-text
                        :label="$t('commons.object')"
                        v-model="amendment.object"
                        class="col-span-2 md:col-span-1"
                        :required="true"
                        @blur="save()"
                        ref="objectInput"
                    />
                    <app-input-text
                        :label="$t('commons.code')"
                        v-model="amendment.code"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-text-editor
                        :label="$t('amendments.description') + ' : '"
                        v-model="amendment.description"
                        class="col-span-2"
                        @blur="save"
                        :showToolbar="true"
                        end-point="documents?scope=amendmentsDescription"
                    ></app-text-editor>
                    <app-select
                        :label="$t('amendments.type')"
                        v-model="amendment.type"
                        class="col-span-2"
                        @change="save"
                    >
                        <option value="tma">{{ $t('amendments.types.tma') }}</option>
                        <option value="choice">{{ $t('amendments.types.choice') }}</option>
                        <option value="conceptionError">{{ $t('amendments.types.conceptionError') }}</option>
                        <option value="executionError">{{ $t('amendments.types.executionError') }}</option>
                        <option value="unexpected">{{ $t('amendments.types.unexpected') }}</option>
                        <option value="market">{{ $t('amendments.types.market') }}</option>
                        <option value="marketOption">{{ $t('amendments.types.marketOption') }}</option>
                        <option value="marketUpdate">{{ $t('amendments.types.marketUpdate') }}</option>
                        <option value="other">{{ $t('amendments.types.other') }}</option>
                    </app-select>
                    <app-bundle-picker
                        class="col-span-2"
                        v-model="amendment.bundle"
                        :options="bundles"
                        @input="onBundleChange"
                    ></app-bundle-picker>

                    <app-input-text
                        :label="$t('amendments.quoteReference')"
                        v-model="amendment.quoteReference"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-input-text
                        :label="$t('amendments.TMAReference')"
                        v-model="amendment.TMAReference"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-multi-picker
                        class="col-span-2"
                        v-model="amendment.locations"
                        :options="locations"
                        :show-label="true"
                        label-key="fullName"
                        @input="save()"
                        :label="$t('commons.locations')"
                        :title="$t('commons.chooseLocations')"
                    ></app-multi-picker>
                    <app-number-input
                        :label="$t('commons.amountHT')"
                        v-model="amendment.amountHT"
                        :allowsNegative="true"
                        @blur="save()"
                        :currency="currencyFormat.currency"
                    />
                    <app-number-input
                        :label="$t('commons.amountTTC')"
                        v-model="amendment.amountTTC"
                        :allowsNegative="true"
                        @blur="save()"
                        :currency="currencyFormat.currency"
                    />
                    <div v-if="amendment.bundleId">
                        <div class="flex gap2">
                            <app-select
                                :label="$t('amendments.subcontractor')"
                                :value="amendment.subcontractorId"
                                @input="saveSubContractor"
                            >
                                <option :value="null" class="italic">
                                    -- {{ $t('amendments.noSubcontractor') }} --
                                </option>
                                <option value="newSubcontractor" class="italic">
                                    {{ $t('amendments.newSubcontractor') }}...
                                </option>
                                <template v-for="subcontractor in amendment.bundle?.subcontractors">
                                    <option :value="subcontractor.id">{{ subcontractor.name }}</option>
                                </template>
                            </app-select>
                            <div class="flex items-end p-2">
                                <app-tips v-if="amendment.bundleId && amendment.subcontractorId">
                                    <span
                                        class="text-green-600"
                                        v-if="subcontractingApproval.length"
                                        :title="$t('administrativeFiles.validValidatedSubcontractingApproval')"
                                    >
                                        <icon-check-circle-outline></icon-check-circle-outline>
                                    </span>
                                    <span
                                        class="text-red-600"
                                        v-else
                                        :title="$t('administrativeFiles.missingValidatedSubcontractingApproval')"
                                    >
                                        <icon-alert-circle></icon-alert-circle>
                                    </span>
                                </app-tips>
                            </div>
                        </div>
                        <app-checkbox
                            v-if="amendment.subcontractorId"
                            v-model="amendment.hasPaymentDelegation"
                            :label="$t('amendments.hasPaymentDelegation')"
                        ></app-checkbox>
                    </div>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('commons.dates')">
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="amendment.emissionDueDate"
                        :label="$t('commons.emissionDueDate')"
                        @input="save()"
                    ></app-date-input>
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="amendment.emissionDate"
                        :label="$t('amendments.emissionDate')"
                        @input="save()"
                    />
                </app-fieldset>
                <app-save-on-leave :dirty="dirty" :saveFn="() => save(true)"></app-save-on-leave>

                <div class="col-span-2 mt-5">
                    <app-separator :label="$t('commons.status')" class="mt-5"></app-separator>
                    <table class="w-full border">
                        <tr class="border-b">
                            <td class="p-2 px-4 text-left">
                                {{ $t('amendments.quoteStatusTitle') }}
                            </td>
                            <td class="p-2 px-4 text-left">
                                <div class="flex flex-col gap-2">
                                    <app-select
                                        :label="$t('amendments.quoteStatusTitle')"
                                        :show-label="false"
                                        v-model="amendment.quoteStatus"
                                        @input="save()"
                                    >
                                        <option value="toEmit">{{ $t('amendments.quoteStatus.toEmit') }}</option>
                                        <option value="toAnalyse">{{ $t('amendments.quoteStatus.toAnalyse') }}</option>
                                        <option value="toNegotiate">
                                            {{ $t('amendments.quoteStatus.toNegotiate') }}
                                        </option>
                                        <option value="toValidateMOA">
                                            {{ $t('amendments.quoteStatus.toValidateMOA') }}
                                        </option>
                                        <option value="validated">{{ $t('amendments.quoteStatus.validated') }}</option>
                                        <option value="rejected">{{ $t('amendments.quoteStatus.rejected') }}</option>
                                        <option value="obsolete">{{ $t('amendments.quoteStatus.obsolete') }}</option>
                                    </app-select>
                                    <app-label
                                        :label="$t('amendments.quoteComment')"
                                        v-if="
                                            amendment.quoteStatus === 'rejected' || amendment.quoteStatus === 'obsolete'
                                        "
                                    >
                                        <app-textarea v-model="amendment.quoteComment" @blur="save()"></app-textarea>
                                    </app-label>
                                </div>
                            </td>
                        </tr>
                        <tr class="border-b">
                            <td class="p-2 px-4 text-left">
                                {{
                                    amendment.type === 'market'
                                        ? $t('amendments.marketStatus')
                                        : $t('amendments.amendmentStatus')
                                }}
                            </td>
                            <td class="p-2 px-4 text-left">
                                <app-select
                                    :label="
                                        amendment.type === 'market'
                                            ? $t('amendments.marketStatus')
                                            : $t('amendments.amendmentStatus')
                                    "
                                    :show-label="false"
                                    v-model="amendment.amendmentStatus"
                                    @input="save('amendmentStatus', $event)"
                                    :disabled="amendment.quoteStatus === 'toEmit'"
                                >
                                    <option value=""></option>
                                    <option value="toEmit">{{ $t('amendments.status.toEmit') }}</option>
                                    <option value="toSignByBundle">{{ $t('amendments.status.toSignByBundle') }}</option>
                                    <option value="toSignByMOE">{{ $t('amendments.status.toSignByMOE') }}</option>
                                    <option value="toSignByMOA">{{ $t('amendments.status.toSignByMOA') }}</option>
                                    <option value="signed">{{ $t('amendments.status.signed') }}</option>
                                    <option value="canceled">{{ $t('amendments.status.canceled') }}</option>
                                </app-select>
                            </td>
                        </tr>
                        <tr class="border-b">
                            <td class="p-2 px-4 text-left">{{ $t('amendments.SOStatus') }}</td>
                            <td class="p-2 px-4 text-left">
                                <app-select
                                    :label="$t('amendments.SOStatus')"
                                    :show-label="false"
                                    v-model="amendment.SOStatus"
                                    @input="save('SOStatus', $event)"
                                    :disabled="amendment.quoteStatus === 'toEmit'"
                                >
                                    <option value=""></option>
                                    <option value="toEmit">{{ $t('amendments.status.toEmit') }}</option>
                                    <option value="toSignByBundle">{{ $t('amendments.status.toSignByBundle') }}</option>
                                    <option value="toSignByMOE">{{ $t('amendments.status.toSignByMOE') }}</option>
                                    <option value="toSignByMOA">{{ $t('amendments.status.toSignByMOA') }}</option>
                                    <option value="signed">{{ $t('amendments.status.signed') }}</option>
                                </app-select>
                            </td>
                        </tr>
                    </table>
                </div>
            </form>

            <app-popup
                :title="$t('bundles.newSubcontractor')"
                ref="subcontractorPopup"
                :show-header="true"
                popup-extra-class="left-50"
            >
                <div class="m-2">
                    <app-input-text
                        ref="inputField"
                        v-model="subcontractorName"
                        :label="$t('commons.name')"
                        @keypress.enter="
                            $refs.subcontractorPopup.close();
                            validateSubcontractorCreation();
                        "
                        @keypress.esc="$refs.subcontractorPopup.close()"
                    ></app-input-text>
                </div>
                <app-footer
                    @click="
                        $refs.subcontractorPopup.close();
                        validateSubcontractorCreation();
                    "
                    class="w-full p-2"
                >
                    <template v-slot:left>
                        <app-button
                            variant="default"
                            :label="$t('commons.cancel')"
                            size="mini"
                            @click="$refs.subcontractorPopup.close()"
                        />
                    </template>
                </app-footer>
            </app-popup>
        </ValidationObserver>
    </div>
</template>

<script>
import AppLegend from '../../components/appLegend/AppLegend';
import { getMapById, sanitize } from '@/services/sanitize.service';
import AppSelect from '../../components/appSelect/AppSelect';
import AppInputText from '../../components/appInputText/AppInputText';
import AppFieldset from '../../components/appFieldset/AppFieldset';
import AppFooter from '../../components/appFooter/AppFooter';
import AppErrors from '../../components/appErrors/AppErrors';
import AppDateInput from '../../components/appDateInput/AppDateInput';
import AppPicker from '../../components/appPicker/AppPicker';
import AppButton from '../../components/appButton/AppButton';
import AppDurationInput from '../../components/appDurationInput/AppDurationInput';
import { createAmendment, getAmendment, getAmendments, updateAmendment } from './amendments.service';

import AppMultiPicker from '../../components/appMultiPicker/AppMultiPicker';
import AppBundlePicker from '../../components/appBundlePicker';
import AppSaveOnLeave from '@/components/AppSaveOnLeave';
import AppTips from '@/components/app-tips/AppTips';
import AppAutocomplete from '@/components/app-autocomplete/AppAutocomplete';
import AppSeparator from '@/components/appSeparator/AppSeparator';
import AppBundle from '@/components/app-bundle/appBundle';
import AppDurationLink from '@/components/appDurationLink/AppDurationLink';
import { combineLatest, map } from 'rxjs';
import { createSubcontractors, fetchSubcontractors, getBundles } from '@/features/bundles/bundles.service';
import { getLocationsTree } from '@/features/locations/locations.service';
import locationService from '@/services/location.service';
import { createAmendmentVisa, getAmendmentVisas } from '@/features/amendments/amendmentVisas.service';
import { isMobile } from '@/state/state';
import AppLabel from '@/components/appLabel/AppLabel.vue';
import { getCompanies } from '@/features/companies/companies.service';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput.vue';
import AmendmentAttachments from '@/features/amendments/AmendmentAttachments.vue';
import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import AppTextEditor from '@/components/appTextEditor/AppTextEditor.vue';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink.vue';
import IconChevronDown from '@/icons/IconChevronDown.vue';
import { queryProject } from '@/features/projects/projects.service';
import AppCheckbox from '@/components/app-checkbox/AppCheckbox.vue';
import AppPhotoEditor from '@/components/appPhotoEditor/AppPhotoEditor.vue';
import AppPopup from '@/components/app-popup/AppPopup.vue';
import IconAlertCircle from '@/icons/IconAlertCircle.vue';
import IconCheckCircleOutline from '@/icons/IconCheckCircleOutline.vue';
import { getPreparations } from '@/features/preparations/preparations.service';

export default {
    components: {
        IconCheckCircleOutline,
        IconAlertCircle,
        AppPopup,
        AppPhotoEditor,
        AppCheckbox,
        IconChevronDown,
        AppNumberLink,
        AppTextEditor,
        AppTextarea,
        AmendmentAttachments,
        AppNumberInput,
        AppLabel,
        AppDurationLink,
        AppBundle,
        AppSeparator,
        AppAutocomplete,
        AppTips,
        AppSaveOnLeave,
        AppBundlePicker,
        AppMultiPicker,
        AppDurationInput,
        AppButton,
        AppPicker,
        AppDateInput,
        AppErrors,
        AppFooter,
        AppFieldset,
        AppInputText,
        AppSelect,
        AppLegend,
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
        });
        this.init();
        localStorage.setItem(
            'amendment.lastVisitedAmendmentId.' + this.$route.params.projectId,
            this.$route.params.amendmentId,
        );
    },
    watch: {
        $route: function (from, to) {
            if (from.name !== to.name || from.params.amendmentId !== to.params.amendmentId) {
                this.focusDone = false;
            }
            this.init();
        },
    },
    computed: {
        subcontractingApproval() {
            return this.administrativeFiles.filter(
                (administrativeFile) =>
                    administrativeFile.type === 'subcontractingApproval' &&
                    administrativeFile.bundleId === this.amendment.bundleId &&
                    administrativeFile.subcontractorId === this.amendment.subcontractorId &&
                    administrativeFile.visas.every(
                        (visa) => visa.conclusion === 'approved' || visa.conclusion === 'approvedWithComments',
                    ),
            );
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getLocationsTree(this.$route.params.projectId).pipe(
                        map((folders) => locationService.buildLocationOptions(folders)),
                    ),
                    getAmendment(this.$route.params.projectId, this.$route.params.amendmentId),
                    getAmendments(this.$route.params.projectId),
                    getAmendmentVisas(this.$route.params.projectId, this.$route.params.amendmentId),
                    getPreparations(this.$route.params.projectId, 'administrative'),
                ]).subscribe(
                    async ([bundles, companies, locations, amendment, amendments, visas, administrativeFiles]) => {
                        this.locations = locations;
                        this.administrativeFiles = administrativeFiles;
                        const marketSignedAmendments = amendments.filter(
                            (amendment) => amendment.type === 'market' && amendment.amendmentStatus === 'signed',
                        );
                        const locationMap = getMapById(locations);
                        const companyMap = getMapById(companies);
                        const mappedBundles = bundles.map((bundle) => ({
                            ...bundle,
                            company: companyMap[bundle.companyId],
                            marketAmount: marketSignedAmendments.reduce((acc, amendment) => {
                                if (amendment.bundleId === bundle.id) {
                                    return acc + amendment.amountTTC;
                                } else {
                                    return acc;
                                }
                            }, 0),
                        }));
                        this.bundleMap = getMapById(mappedBundles);
                        const subcontractors = amendment.bundleId
                            ? await fetchSubcontractors(this.$route.params.projectId, amendment.bundleId)
                            : [];
                        this.amendmentVisas = visas.map((visa) => ({
                            ...visa,
                            emitter: this.bundleMap[visa.emitterId],
                        }));
                        this.amendment = {
                            ...amendment,
                            bundle: amendment.bundleId
                                ? {
                                      ...mappedBundles.find((bundle) => bundle.id === amendment.bundleId),
                                      subcontractors,
                                  }
                                : { subcontractors: [] },
                            locations: amendment.locationIds ? amendment.locationIds.map((id) => locationMap[id]) : [],
                        };
                        this.bundles = mappedBundles;
                        this.selectedVersion = this.$route.params.amendmentId;
                        if (!this.focusDone && !isMobile && this.amendment.object === '') {
                            this.$nextTick(() => {
                                this.$refs.objectInput.focus();
                            });
                            this.focusDone = true;
                        }
                        if (this.amendment.distribution) {
                            this.selectedBundles = this.amendment.distribution.map(
                                (line) => this.bundleMap[line.bundleId],
                            );
                            this.distribution = this.amendment.distribution.map((line) => ({
                                ...line,
                                bundle: this.bundleMap[line.bundleId],
                            }));
                        } else {
                            this.selectedBundles = [];
                            this.distribution = [];
                        }
                    },
                ),
            ];
        },
        onBundleChange() {
            this.save();
        },
        async onVersionSelected(amendmentId) {
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    amendmentId,
                },
            });
        },
        async onNewVersion() {
            const amendmentEntity = {
                groupId: this.amendment.groupId,
                object: this.amendment.object,
                description: this.amendment.description,
                type: this.amendment.type,
                code: this.amendment.code,
                emissionDate: null,
                emissionDueDate: null,
                bundleId: this.amendment.bundle ? this.amendment.bundle.id : null,
            };
            const result = await createAmendment(this.$route.params.projectId, amendmentEntity);
            await Promise.all(
                this.amendmentVisas
                    .filter((visa) => visa.emitterId)
                    .map((visa) => {
                        return createAmendmentVisa(this.$route.params.projectId, {
                            emitterId: visa.emitterId,
                            amendmentId: result.id,
                        });
                    }),
            );
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    amendmentId: result.id,
                },
            });
        },
        sanitizeAmendment(amendment, toOmit = []) {
            return {
                ...sanitize(amendment, [
                    'bundle',
                    'locations',
                    'visas',
                    'documents',
                    'versions',
                    'conclusion',
                    'createdAt',
                    ...toOmit,
                ]),
                bundleId: amendment.bundle ? amendment.bundle.id : null,
                locationIds: amendment.locations.map((location) => location.id),
                emissionDueDate: amendment.emissionDueDate || null,
                subcontractorId: amendment.subcontractorId || null,
            };
        },
        async validateSubcontractorCreation() {
            const subcontractor = await createSubcontractors(
                this.$route.params.projectId,
                this.amendment.bundleId,
                this.subcontractorName,
            );
            this.amendment.subcontractorId = subcontractor.id;
            this.save();
        },
        saveSubContractor(subcontractorId) {
            if (subcontractorId === 'newSubcontractor') {
                this.subcontractorName = '';
                this.$refs.subcontractorPopup.open();
                setTimeout(() => this.$refs.inputField.focus(), 200);
            } else {
                this.amendment.subcontractorId = subcontractorId || null;
                this.save();
            }
        },
        async save() {
            if (this.savingFlag) {
                return;
            } else {
                this.savingFlag = true;
            }
            try {
                const amendmentEntity = this.sanitizeAmendment(this.amendment);
                await updateAmendment(this.$route.params.projectId, {
                    id: this.amendment.id,
                    ...amendmentEntity,
                    distribution: this.distribution.map((distribution) => ({
                        bundleId: distribution.bundleId,
                        amount: distribution.amount,
                    })),
                });
                if (this.$refs.observer) {
                    this.$refs.observer.reset();
                }
            } finally {
                this.savingFlag = false;
            }
        },
        async createNew() {
            const result = await createAmendment(this.$route.params.projectId, {
                object: '',
            });
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    amendmentId: result.id,
                },
            });
        },
    },
    data() {
        return {
            administrativeFiles: [],
            subcontractorName: '',
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            focusDone: false,
            distribution: [],
            subscriptions: [],
            bundleMap: {},
            savingFlag: false,
            amendmentReviewMargin: 0,
            bundles: [],
            bundlesToCopy: [],
            amendmentVisas: [],
            amendment: {
                object: '',
                locations: [],
                locationIds: [],
                bundle: null,
                documents: [],
                versions: [],
                deliveryDuration: 0,
                hasPaymentDelegation: false,
                subcontractorId: null,
            },
            selectedVersion: null,
            project: {},
            locations: [],
            selectedBundles: [],
        };
    },
};
</script>
